import { apiWithEntityId, apiWithMSO } from 'services/api';

import { useStateValue } from 'state';
import { POS_CONFIG } from '../type';
import {
  action,
  actionFailure,
  actionSuccess,
  CREATE_POS_CONFIG,
  DELETE_POS_CONFIG,
  FETCH_ENABLED_PROVIDERS,
  FETCH_GUEST_LIST,
  FETCH_POS_CONFIG,
  FETCH_POS_CONFIG_LIST,
  UPDATE_POS_CONFIG,
} from '../actions';
import { string } from 'prop-types';
import { AxiosError } from 'axios';

const usePosConfig = () => {
  const { state, dispatch } = useStateValue();
  const { posConfig } = state;

  const toFetchEnabledProviders = async () => {
    dispatch(action(FETCH_ENABLED_PROVIDERS));
    try {
      const response = await apiWithEntityId.get(`/providers/enabled?withSecrets=true`);
      dispatch(actionSuccess(FETCH_ENABLED_PROVIDERS, response?.data));
    } catch (e) {
      dispatch(actionFailure(FETCH_ENABLED_PROVIDERS, e));
    }
  };

  const toCreatePosConfig = async (values: POS_CONFIG.ICreatePosConfig) => {
    dispatch(action(CREATE_POS_CONFIG));
    try {
      const response = await apiWithEntityId.post(
        `/providerConfigurations`,
        values
      );
      dispatch(actionSuccess(CREATE_POS_CONFIG, response?.data));
    } catch (e) {
      dispatch(actionFailure(CREATE_POS_CONFIG, e));
    }
  };

  const toFetchPosConfig = async (id: string) => {
    dispatch(action(FETCH_POS_CONFIG));
    try {
      const response = await apiWithEntityId.get(
        `/providerConfigurations/${id}`
      );
      dispatch(actionSuccess(FETCH_POS_CONFIG, response?.data));
    } catch (e) {
      dispatch(actionFailure(FETCH_POS_CONFIG, e));
    }
  };

  const toFetchPosConfigList = async (providerId?: string, code?: string) => {
    dispatch(action(FETCH_POS_CONFIG_LIST));
    try {
      const response = await apiWithEntityId.get(
        `/providerConfigurations${
          providerId ? `?providerId=${providerId}` : ''
        }${code ? `&code=${code}` : ''}`
      );
      dispatch(actionSuccess(FETCH_POS_CONFIG_LIST, response?.data));
    } catch (e) {
      dispatch(actionFailure(FETCH_POS_CONFIG_LIST, e));
    }
  };

  const toUpdatePosConfig = async (
    values: POS_CONFIG.IUpdatePosConfig
  ) => {
    dispatch(action(UPDATE_POS_CONFIG));
    try {
      const response = await apiWithEntityId.put(
        `/providerConfigurations`,
        { ...values }
      );
      dispatch(actionSuccess(UPDATE_POS_CONFIG, response?.data));
    } catch (e) {
      dispatch(actionFailure(UPDATE_POS_CONFIG, e));
    }
  };

  const toDeletePosConfig = async (id: string) => {
    dispatch(action(DELETE_POS_CONFIG));
    try {
      const response = await apiWithEntityId.remove(
        `/providerConfigurations/${id}`
      );
      dispatch(actionSuccess(DELETE_POS_CONFIG, response?.data));
    } catch (e) {
      dispatch(actionFailure(DELETE_POS_CONFIG, e));
    }
  };

  const toFetchGuestList = async (idOrName:string) => {
    dispatch(action(FETCH_GUEST_LIST));
    try {
      const response = await apiWithEntityId.post(`/integrations/${idOrName}/guestList`);
      dispatch(actionSuccess(FETCH_GUEST_LIST, response?.data));
    } catch (e) {
      dispatch(actionFailure(FETCH_GUEST_LIST, e));
    }
  };

  const toTestAuthCredentials = async (idOrName:string) : Promise<{success: boolean, message: string}> => {
    try {
      const response = await apiWithEntityId.post(`/integrations/${idOrName}/testAuthCreds`);
      return { success: true, message: 'Credentials validated successfully!' };
    } catch (e) {
      let message = 'Failed to validate credentials. Please check your input and try again.';
      if (e instanceof AxiosError) { 
        message = e.response?.data?.message || message;
      }
      console.error('Error testing auth credentials:', e);
      return { 
        success: false, 
        message: message
      };
    }
  };

  return {
    posConfig,
    isLoading: posConfig.isLoading > 0,
    toFetchPosConfig,
    toCreatePosConfig,
    toUpdatePosConfig,
    toDeletePosConfig,
    toFetchEnabledProviders,
    toFetchPosConfigList,
    toFetchGuestList,
    toTestAuthCredentials
  };
};

export default usePosConfig;
